import { useEffect, useState, useCallback, useRef } from "react";
import { X, Calendar as CalendarIcon, User, Scissors, Clock, AlertCircle, CheckCircle2, Loader2, Search } from "lucide-react";
import api from "@/lib/api";

/**
 * Global modal that lets a salon-admin / staff member create a booking
 * on behalf of a walk-in or phone customer. Mounted in CmsLayout so it's
 * available from every CMS page.
 */
export default function ManualBookingDialog({ open, onClose, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();
  const isPastSlot = (date, t) => {
    if (date !== today) return false;
    const [h, m] = (t || "").split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return false;
    return h * 60 + m < nowMinutes;   // strictly before "now"
  };
  const minTimeForDate = (date) => (date === today
    ? `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`
    : undefined);
  const [services, setServices] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);

  const [form, setForm] = useState({
    service_id: "",
    staff_id: "",
    customer_id: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    date: today,
    time: "",
    notes: "",
    addon_ids: [],
    send_customer_email: true,
    skip_conflict_check: false,
  });
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);
  const [custDropdownOpen, setCustDropdownOpen] = useState(false);
  const custSearchTimer = useRef(null);

  const reset = () => {
    setForm({
      service_id: "", staff_id: "", customer_id: "", customer_name: "", customer_phone: "", customer_email: "",
      date: today, time: "", notes: "", addon_ids: [], send_customer_email: true, skip_conflict_check: false,
    });
    setSlots([]); setErr(""); setDone(null);
    setCustQuery(""); setCustResults([]); setCustDropdownOpen(false);
    setAddons([]);
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setForm({
      service_id: "", staff_id: "", customer_id: "", customer_name: "", customer_phone: "", customer_email: "",
      date: today, time: "", notes: "", addon_ids: [], send_customer_email: true, skip_conflict_check: false,
    });
    setSlots([]); setErr(""); setDone(null);
    setCustQuery(""); setCustResults([]); setCustDropdownOpen(false);
    setAddons([]);
    let cancelled = false;
    Promise.all([api.get("/cms/services"), api.get("/cms/staff")]).then(([s, st]) => {
      if (cancelled) return;
      setServices((s.data || []).filter((x) => x.active !== false));
      setStaffList((st.data || []).filter((x) => x.active !== false));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadSlots = useCallback(async () => {
    if (!form.service_id || !form.date) { setSlots([]); return; }
    setLoadingSlots(true);
    try {
      const params = new URLSearchParams({ service_id: form.service_id, date: form.date });
      if (form.staff_id) params.append("staff_id", form.staff_id);
      if ((form.addon_ids || []).length > 0) params.append("addon_ids", form.addon_ids.join(","));
      const { data } = await api.get(`/cms/bookings/slots?${params}`);
      setSlots(data.slots || []);
    } catch { setSlots([]); }
    finally { setLoadingSlots(false); }
  }, [form.service_id, form.staff_id, form.date, form.addon_ids]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  // Load add-ons for the picked service
  useEffect(() => {
    if (!form.service_id) { setAddons([]); return undefined; }
    let cancelled = false;
    setLoadingAddons(true);
    api.get(`/cms/services/${form.service_id}/addons`)
      .then(({ data }) => {
        if (cancelled) return;
        const active = (data || []).filter((a) => a.active !== false);
        setAddons(active);
        // Drop any previously-selected addon-ids that don't belong to this service
        setForm((f) => ({ ...f, addon_ids: (f.addon_ids || []).filter((id) => active.some((a) => a.id === id)) }));
      })
      .catch(() => { if (!cancelled) setAddons([]); })
      .finally(() => { if (!cancelled) setLoadingAddons(false); });
    return () => { cancelled = true; };
  }, [form.service_id]);

  // Debounced customer search
  useEffect(() => {
    if (!open) return undefined;
    if (custSearchTimer.current) clearTimeout(custSearchTimer.current);
    const q = custQuery.trim();
    if (q.length < 2) { setCustResults([]); setCustSearching(false); return undefined; }
    setCustSearching(true);
    custSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cms/customers/search?q=${encodeURIComponent(q)}`);
        setCustResults(Array.isArray(data) ? data : []);
      } catch { setCustResults([]); }
      finally { setCustSearching(false); }
    }, 250);
    return () => { if (custSearchTimer.current) clearTimeout(custSearchTimer.current); };
  }, [custQuery, open]);

  const pickCustomer = (c) => {
    setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name || "", customer_phone: c.phone || "", customer_email: c.email || "" }));
    setCustQuery(""); setCustResults([]); setCustDropdownOpen(false);
  };
  const clearCustomer = () => {
    setForm((f) => ({ ...f, customer_id: "", customer_name: "", customer_phone: "", customer_email: "" }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      const payload = {
        service_id: form.service_id,
        staff_id: form.staff_id || null,
        customer_id: form.customer_id || null,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim() || "",
        date: form.date,
        time: form.time,
        notes: form.notes.trim(),
        send_customer_email: form.send_customer_email,
        skip_conflict_check: form.skip_conflict_check,
        addon_ids: form.addon_ids || [],
      };
      const { data } = await api.post("/cms/bookings/manual", payload);
      setDone(data.booking);
      if (onCreated) onCreated(data.booking);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Fehler beim Anlegen des Termins.");
    } finally { setSubmitting(false); }
  };

  if (!open) return null;

  const selectedService = services.find((s) => s.id === form.service_id);
  const canSubmit = form.service_id && form.customer_name && form.date && form.time && !submitting;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onClose}
      data-testid="manual-booking-backdrop"
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl"
        style={{ background: "var(--mv-surface)", borderRadius: 20 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="manual-booking-dialog"
      >
        <div className="flex items-center justify-between p-5 border-b sticky top-0 z-10" style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}>
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
            <h2 className="font-display text-2xl">Neuer Termin</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[var(--mv-surface-2)]" data-testid="manual-booking-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" strokeWidth={1.5} style={{ color: "#10B981" }} />
            <h3 className="font-display text-2xl mb-2">Termin angelegt</h3>
            <p className="text-sm mb-6 opacity-75">{done.customer_name} · {done.service_name} · {done.date} um {done.time} Uhr</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => { reset(); }} className="mv-btn-ghost text-sm" data-testid="manual-booking-create-another">Weiteren Termin anlegen</button>
              <button onClick={onClose} className="mv-btn text-sm" data-testid="manual-booking-finish">Schließen</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-5">
            {err && (
              <div className="flex items-start gap-2 p-3 rounded-md text-sm" style={{ background: "#FEF2F2", color: "#991B1B" }} data-testid="manual-booking-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span>{err}</span>
              </div>
            )}

            <Section icon={Scissors} title="Leistung">
              <select
                required
                className="mv-input"
                value={form.service_id}
                onChange={(e) => set("service_id", e.target.value)}
                data-testid="manual-booking-service-select"
              >
                <option value="">Leistung wählen…</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.duration_minutes} Min · {Number(s.price || 0).toFixed(2)} €
                  </option>
                ))}
              </select>
              <select
                className="mv-input mt-2"
                value={form.staff_id}
                onChange={(e) => set("staff_id", e.target.value)}
                data-testid="manual-booking-staff-select"
              >
                <option value="">Mitarbeiter:in (optional — automatisch)</option>
                {staffList.map((s) => <option key={s.id} value={s.id}>{s.name}{s.role ? ` — ${s.role}` : ""}</option>)}
              </select>

              {/* Add-ons (optional, shown only when the selected service has any) */}
              {form.service_id && (
                <div className="mt-3" data-testid="manual-booking-addons-section">
                  <p className="text-[11px] uppercase tracking-wider mb-1.5 opacity-70">Zusatzleistungen (optional)</p>
                  {loadingAddons ? (
                    <p className="text-xs opacity-60 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Lade Zusatzleistungen…</p>
                  ) : addons.length === 0 ? (
                    <p className="text-xs italic opacity-60">Für diese Leistung sind keine Zusatzleistungen hinterlegt.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="manual-booking-addons-list">
                      {addons.map((a) => {
                        const checked = form.addon_ids.includes(a.id);
                        return (
                          <label
                            key={a.id}
                            data-testid={`manual-booking-addon-${a.id}`}
                            className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border cursor-pointer hover:bg-[var(--mv-surface-2)]"
                            style={{ borderColor: checked ? "var(--mv-primary)" : "var(--mv-border)", background: checked ? "var(--mv-surface)" : "transparent" }}
                          >
                            <span className="flex items-center gap-2 text-sm min-w-0">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...form.addon_ids, a.id]
                                    : form.addon_ids.filter((id) => id !== a.id);
                                  setForm((f) => ({ ...f, addon_ids: next, time: "" /* slot may shift */ }));
                                }}
                              />
                              <span className="truncate">{a.name}</span>
                            </span>
                            <span className="text-xs opacity-70 whitespace-nowrap">
                              {a.duration_minutes ? `+${a.duration_minutes} Min` : ""} {a.price ? `· +${Number(a.price).toFixed(2)} €` : ""}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </Section>

            <Section icon={User} title="Kundendaten">
              {/* Search for existing registered customer */}
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" strokeWidth={1.6} />
                <input
                  type="search"
                  className="mv-input pl-9"
                  placeholder="Bestehenden Kunden suchen (Name, E-Mail, Telefon)…"
                  value={custQuery}
                  onChange={(e) => { setCustQuery(e.target.value); setCustDropdownOpen(true); }}
                  onFocus={() => setCustDropdownOpen(true)}
                  data-testid="manual-booking-customer-search"
                />
                {custDropdownOpen && (custQuery.trim().length >= 2) && (
                  <div
                    className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-md border shadow-lg"
                    style={{ background: "var(--mv-surface)", borderColor: "var(--mv-border)" }}
                    data-testid="manual-booking-customer-results"
                  >
                    {custSearching ? (
                      <div className="p-3 text-xs flex items-center gap-2 opacity-70"><Loader2 className="w-3 h-3 animate-spin" /> Suche…</div>
                    ) : custResults.length === 0 ? (
                      <div className="p-3 text-xs opacity-70">Keine registrierten Kunden gefunden. Felder unten ausfüllen für neuen Kunden.</div>
                    ) : custResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickCustomer(c)}
                        data-testid={`manual-booking-customer-pick-${c.id}`}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--mv-surface-2)] flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="font-medium truncate">{c.name || "(ohne Name)"}</p>
                          <p className="text-xs opacity-70 truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                        </div>
                        {c.loyalty_points > 0 && (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--mv-primary)", color: "#fff" }}>{c.loyalty_points} P.</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {form.customer_id && (
                <div className="flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-md text-xs" style={{ background: "var(--mv-surface-2)" }} data-testid="manual-booking-customer-selected">
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color: "#10B981" }} /> Bestehender Kunde ausgewählt</span>
                  <button type="button" onClick={clearCustomer} className="underline opacity-70 hover:opacity-100" data-testid="manual-booking-customer-clear">Auswahl entfernen</button>
                </div>
              )}

              <input
                required
                className="mv-input"
                placeholder="Name *"
                value={form.customer_name}
                onChange={(e) => set("customer_name", e.target.value)}
                data-testid="manual-booking-customer-name"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <input
                  className="mv-input"
                  placeholder="Telefon"
                  value={form.customer_phone}
                  onChange={(e) => set("customer_phone", e.target.value)}
                  data-testid="manual-booking-customer-phone"
                />
                <input
                  className="mv-input"
                  type="email"
                  placeholder="E-Mail (optional)"
                  value={form.customer_email}
                  onChange={(e) => set("customer_email", e.target.value)}
                  data-testid="manual-booking-customer-email"
                />
              </div>
            </Section>

            <Section icon={Clock} title="Datum & Uhrzeit">
              <div className="flex gap-2 items-center mb-3">
                <input
                  type="date"
                  required
                  min={today}
                  className="mv-input"
                  value={form.date}
                  onChange={(e) => { set("date", e.target.value); set("time", ""); }}
                  data-testid="manual-booking-date"
                />
                <button
                  type="button"
                  className="mv-btn-ghost text-xs whitespace-nowrap"
                  onClick={() => { set("date", today); set("time", ""); }}
                  data-testid="manual-booking-today"
                >Heute</button>
              </div>
              {loadingSlots ? (
                <div className="flex items-center gap-2 text-sm opacity-70 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Lade verfügbare Zeiten...</div>
              ) : !form.service_id ? (
                <p className="text-sm italic opacity-70">Bitte zuerst eine Leistung wählen.</p>
              ) : slots.length === 0 ? (
                <div>
                  <p className="text-sm italic opacity-70 mb-2">Keine freien Zeiten an diesem Tag (Öffnungszeiten, Urlaub oder vollständig gebucht).</p>
                  <input
                    type="time"
                    min={minTimeForDate(form.date)}
                    className="mv-input"
                    placeholder="Uhrzeit manuell eintragen"
                    value={form.time}
                    onChange={(e) => set("time", e.target.value)}
                    data-testid="manual-booking-time-manual"
                  />
                  <p className="text-[11px] opacity-60 mt-1.5">Manuelle Eingabe = Doppelbuchung möglich. Aktiviere unten &bdquo;Konflikte ignorieren&ldquo; wenn nötig.</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-1" data-testid="manual-booking-slots">
                  {slots.map((t) => {
                    const past = isPastSlot(form.date, t);
                    const selected = form.time === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        disabled={past}
                        title={past ? "Slot liegt in der Vergangenheit" : undefined}
                        onClick={() => !past && set("time", t)}
                        data-testid={`manual-booking-slot-${t}`}
                        className="px-2 py-1.5 text-sm rounded border transition-colors disabled:cursor-not-allowed"
                        style={{
                          background: selected ? "var(--mv-primary)" : "transparent",
                          color: selected ? "#fff" : (past ? "var(--mv-text-muted)" : "var(--mv-text)"),
                          borderColor: selected ? "var(--mv-primary)" : "var(--mv-border)",
                          opacity: past ? 0.4 : 1,
                          textDecoration: past ? "line-through" : "none",
                        }}
                      >{t}</button>
                    );
                  })}
                </div>
              )}
            </Section>

            <Section title="Notizen (optional)">
              <textarea
                className="mv-input min-h-[80px]"
                placeholder="z. B. Haarlänge, Wunsch, Hinweise vom Telefonat..."
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                data-testid="manual-booking-notes"
              />
            </Section>

            <div className="space-y-2 text-sm pt-2 border-t" style={{ borderColor: "var(--mv-border)" }}>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.send_customer_email}
                  onChange={(e) => set("send_customer_email", e.target.checked)}
                  data-testid="manual-booking-send-email"
                  className="mt-0.5"
                />
                <span>Bestätigung per E-Mail an Kunden senden (nur wenn E-Mail vorhanden)</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.skip_conflict_check}
                  onChange={(e) => set("skip_conflict_check", e.target.checked)}
                  data-testid="manual-booking-skip-conflict"
                  className="mt-0.5"
                />
                <span>Konflikt-Prüfung überspringen <span className="opacity-60">(Doppelbuchung erzwingen)</span></span>
              </label>
            </div>

            {selectedService && form.time && (() => {
              const pickedAddons = addons.filter((a) => form.addon_ids.includes(a.id));
              const addonDuration = pickedAddons.reduce((s, a) => s + (a.duration_minutes || 0), 0);
              const addonPrice = pickedAddons.reduce((s, a) => s + Number(a.price || 0), 0);
              const totalMin = (selectedService.duration_minutes || 0) + addonDuration;
              const totalPrice = Number(selectedService.price || 0) + addonPrice;
              return (
                <div className="p-3 rounded-md text-sm space-y-1" style={{ background: "var(--mv-surface-2)" }} data-testid="manual-booking-summary">
                  <div>
                    <strong>Übersicht:</strong> {selectedService.name} · {form.date} um {form.time} Uhr
                    {form.staff_id && <> · bei {staffList.find((s) => s.id === form.staff_id)?.name}</>}
                  </div>
                  {pickedAddons.length > 0 && (
                    <div className="text-xs opacity-80">+ {pickedAddons.map((a) => a.name).join(", ")}</div>
                  )}
                  <div className="text-xs opacity-80">Dauer: {totalMin} Min · Preis: {totalPrice.toFixed(2)} €</div>
                </div>
              );
            })()}

            <div className="flex gap-2 justify-end pt-3 border-t" style={{ borderColor: "var(--mv-border)" }}>
              <button type="button" onClick={onClose} className="mv-btn-ghost">Abbrechen</button>
              <button type="submit" disabled={!canSubmit} className="mv-btn" data-testid="manual-booking-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarIcon className="w-4 h-4" />}
                Termin anlegen
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <p className="mv-label flex items-center gap-1.5 mb-2">
        {Icon && <Icon className="w-3 h-3" strokeWidth={1.5} />} {title}
      </p>
      {children}
    </div>
  );
}
